<script setup>
import ModuleListFilter from "~/components/list/ModuleListFilter.vue";
import ModuleBrandOverview from "~/components/brand/ModuleBrandOverview.vue";
import ModuleListBanner from "~/components/list/ModuleListBanner.vue";
import ModuleListProductList from "~/components/list/ModuleListProductList.vue";
import ModulePagination from "~/components/list/ModulePagination.vue";
import ModuleListTitle from "~/components/list/ModuleListTitle.vue";
import {basicProductListByBrand, basicProductListByViavia} from "~/api/api.basic";
import {brandsInfo} from "~/api/api.brands";
import ModuleListEmpty from "~/components/list/ModuleListEmpty.vue";
import {labelFormatter, PAGE_LABEL} from "~/utils/smartlook";
import {useBaseStore} from "~/stores/base";
import ModuleBrandOverviewSkeleton from "~/components/brand/ModuleBrandOverviewSkeleton.vue";

const route = useRoute()

const props = defineProps({
  filter: {
    default: {},
    required: true
  },
  filterCheckedCount: {
    default: 0,
    required: true
  }
})

const isOverviewLoading = ref(false)
const id = route.params.id
const availableNow = ref(false)
const sortBy = ref({label: "For you", value: "forYou"})
const pageIndex = ref(1)
const pageSize = ref(48)
const brandDetail = ref({
  brandHandle: "" // 品牌
})
const banners = ref([])
const list = ref([])
const isReachBottom = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const isLogin = computed(() => useBaseStore().getterIsLogin)

/** watch **/
watch(() => props.filter, () => {
  pageIndex.value = 1
  getList()
})
// 已登录 默认开启availableNow
watch(isLogin, () => {
  if (isLogin.value) availableNow.value = true
}, {immediate: true})

/** methods **/
const addSkeleton = () => {
  const skeletonList = []
  for (let i = 0; i < pageSize.value; i++) {
    skeletonList.push({isLoading: true})
  }
  list.value = skeletonList
}
const removeSkeleton = () => {
  list.value = list.value.filter(item => !item.isLoading)
}
const getOverview = async () => {
  try {
    isOverviewLoading.value = true
    const {data, pending, error, refresh} = await useAsyncData(() => brandsInfo({id}))
    console.log("brandsInfo", data.value)
    brandDetail.value = data.value.result.info
    isOverviewLoading.value = false
  } catch (e) {
    console.error(e)
  }
}
const getBanners = async () => {
  try {
    const {data, pending, error, refresh} = await useAsyncData(() => basicProductListByViavia())
    console.log("basicProductListByViavia", data.value)
    banners.value = data.value.result.imageList
  } catch (e) {
    console.error(e)
  }
}
const getList = async () => {
  addSkeleton()
  const params = {
    brand: brandDetail.value.brandHandle,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    availableNow: availableNow.value,
    orderByColumn: sortBy.value.value,
    filter: props.filter
  }
  console.log("basicProductListByBrand params", params)
  try {
    let res
    if (isLogin.value && process.client) {
      /** 已登录且客户端，必须用这个，避免拉来数据不渲染 **/
      res = await basicProductListByBrand(params)
    } else {
      /** 未登录或服务端渲染，使用 useAsyncData **/
      const {data} = await useAsyncData(() => basicProductListByBrand(params))
      res = data.value
    }
    console.log("basicProductListByBrand", res)
    removeSkeleton()
    list.value = res.result.list
    isReachBottom.value = !res.result.pageIndex
  } catch (e) {
    removeSkeleton()
    console.error(e)
  }
}
const handleUpdate = (e) => {
  availableNow.value = e.availableNow
  sortBy.value = e.sortBy
  pageIndex.value = 1
  getList()
}
const handlePrevious = async () => {
  window.scrollTo({
    top: 0,
    behavior: "auto"
  })
  pageIndex.value--
  await getList()
}
const handleNext = async () => {
  window.scrollTo({
    top: 0,
    behavior: "auto"
  })
  pageIndex.value++
  await getList()
}

/** lifecycle **/
addSkeleton()
await getOverview()
useSeoMeta({
  title: brandDetail.value.name + " | BNTO",
  ogTitle: brandDetail.value.name + " | BNTO",
  description: brandDetail.value.brandBriefIntroductionAndStory,
  ogDescription: brandDetail.value.brandBriefIntroductionAndStory,
  ogImage: brandDetail.value.cover?.url,
  ogType: "brand"
})
/** viavia 专属 **/
if (brandDetail.value.brandHandle === "viavia") {
  getBanners()
}
// 未登录 - SSR，已登录的依赖 DrawerFilter - filterApply获取数据
if (!isLogin.value) getList()
onMounted(() => {
  useBaseStore().updatePageLabel(`${PAGE_LABEL.BRAND}_${labelFormatter(brandDetail.value.brandHandle)}`)
})
onActivated(() => {
  useBaseStore().updatePageLabel(`${PAGE_LABEL.BRAND}_${labelFormatter(brandDetail.value.brandHandle)}`)
})
</script>

<template>
  <div class="brand-content-mobile" v-if="isMobile">
    <module-list-title :title="brandDetail.name"/>
    <module-list-filter :filter-checked-count="filterCheckedCount" :available-now="availableNow" :sort-by="sortBy" @update="handleUpdate"/>

    <!-- 仅第一页提供表头 -->
    <template v-if="pageIndex === 1">
      <module-brand-overview-skeleton v-if="isOverviewLoading"/>
      <module-brand-overview :info="brandDetail" v-else/>
      <!-- viavia 专属 -->
      <module-list-banner :banners="banners" v-if="brandDetail.brandHandle === 'viavia'"/>
    </template>

    <template v-if="list.length">
      <module-list-product-list :list="list"/>
      <module-pagination :page-index="pageIndex" :is-reach-bottom="isReachBottom" @previous="handlePrevious" @next="handleNext"/>
    </template>

    <!-- 空列表 -->
    <module-list-empty v-else/>
  </div>
  <div class="brand-content-desktop" v-else>
    <module-list-title :title="brandDetail.name"/>
    <module-list-filter :filter-checked-count="filterCheckedCount" :available-now="availableNow" :sort-by="sortBy" @update="handleUpdate"/>

    <!-- 仅第一页提供表头 -->
    <template v-if="pageIndex === 1">
      <module-brand-overview-skeleton v-if="isOverviewLoading"/>
      <module-brand-overview :info="brandDetail" v-else/>
      <!-- viavia 专属 -->
      <module-list-banner :banners="banners" v-if="brandDetail.brandHandle === 'viavia'"/>
    </template>

    <template v-if="list.length">
      <module-list-product-list :list="list"/>
      <module-pagination :page-index="pageIndex" :is-reach-bottom="isReachBottom" @previous="handlePrevious" @next="handleNext"/>
    </template>

    <!-- 空列表 -->
    <module-list-empty v-else/>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.brand-content-mobile {

}

.brand-content-desktop {
  min-height: calc(100vh - $desktop-header-height - $desktop-footer-height);
}
</style>
